import { ButtonOutlined } from 'components/Button'
import { animated } from 'react-spring'
import { Text } from 'rebass'
import styled, { keyframes } from 'styled-components'
import { ThemedText } from 'theme'

import { PoolType, typeConfig } from './types'

export const MainContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 24px;
  flex-direction: column;
  margin: 0 auto;
  user-select: none;
  padding: 24px 0;
`

export const PoolCardContainer = styled.div`
  width: 100%;
  display: grid;
  gap: 32px;
  grid-template-columns: repeat(3, 1fr);

  ${({ theme }) => theme.mediaWidth.upToLarge`
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  `}

  ${({ theme }) => theme.mediaWidth.upToMedium`
    grid-template-columns: 1fr;
    gap: 20px;
  `}

  ${({ theme }) => theme.mediaWidth.upToSmall`
    gap: 16px;
  `}
`

export const Header = styled(ThemedText.DeprecatedBody)`
  font-size: 24px;
  font-weight: 600;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 20px;
  `}
`

export const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 40px 20px 20px;
  gap: 20px;
  isolation: isolate;
  position: relative;
  width: 100%;
  min-width: 280px;
  max-width: 100%;
  border: 1px solid rgba(51, 65, 85, 0.24);
  background: linear-gradient(180deg, rgba(29, 40, 58, 0) 40%, rgba(48, 131, 220, 0.2) 100%);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: rgba(51, 65, 85, 0.6);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    transform: translateY(-0.5px);
  }

  ${({ theme }) => theme.mediaWidth.upToMedium`
    padding: 32px 16px 16px;
    gap: 16px;
  `}
`

export const SVGWrapper = styled.div<{ $type: PoolType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;

  svg {
    width: 100%;
    height: 100%;

    path {
      fill: url(#gradient-${({ $type }) => $type});
    }
  }
`

export const TopBadge = styled.div<{ $type: PoolType }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 4px 12px;
  gap: 6px;
  position: absolute;
  height: 24px;
  left: 0px;
  top: 0px;
  background: ${({ $type }) => typeConfig[$type].background};
  border-radius: 4px 0px;
  z-index: 10;
`

export const BadgeText = styled(ThemedText.DeprecatedBody)<{ $type: PoolType }>`
  font-size: 14px;
  font-weight: 600;
  background: ${({ $type }) => typeConfig[$type].textGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`

export const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: flex-end;
  padding: 0px;
  position: relative;
  overflow: hidden;
  min-height: 120px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    min-height: 160px;
  `}
`

export const AnimatedPoolContent = styled(animated.div)`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    gap: 16px;
  `}
`

export const ContentAsset = styled.div`
  display: flex;
  position: relative;
  flex-direction: row;
  align-items: center;
  padding: 0px;
  gap: 12px;
  margin-left: 20px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    gap: 8px;
    margin-left: 16px;
  `}
`

export const InlineText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0px;
  gap: 20px;
  width: 100%;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  `}
`

export const APRContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 6px;
  flex: 1;
`

export const ResponsiveButton = styled(ButtonOutlined)`
  gap: 8px;
  width: auto;
  justify-content: center;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    gap: 4px;
    padding: 8px 16px;
    width: 100%;
  `}
`

export const TokenName = styled(ThemedText.DeprecatedBody)`
  font-size: 18px;
  font-weight: 500;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 16px;
  `}
`

export const APRText = styled(Text)`
  font-size: 20px;
  font-weight: 600;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    font-size: 18px;
  `}
`

export const IndicatorContainer = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  z-index: 5;
`

export const IndicatorButton = styled.button<{ $active: boolean; $type: PoolType }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${({ $active, $type }) => {
    if ($active) {
      switch ($type) {
        case 'major':
          return '#F7931A'
        case 'lst':
          return '#FEF3C7'
        case 'eco':
          return '#AB8F78'
        default:
          return '#F7931A'
      }
    }
    return 'rgba(255, 255, 255, 0.3)'
  }};

  &:hover {
    background: ${({ $active, $type }) => {
      if ($active) return 'current'
      switch ($type) {
        case 'major':
          return 'rgba(247, 147, 26, 0.5)'
        case 'lst':
          return 'rgba(254, 243, 199, 0.5)'
        case 'eco':
          return 'rgba(171, 143, 120, 0.5)'
        default:
          return 'rgba(247, 147, 26, 0.5)'
      }
    }};
    transform: scale(1.2);
  }
`

export const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`

export const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0.1) 100%
  );
  background-size: 200px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 4px;
`

export const SkeletonBadge = styled(SkeletonBase)`
  position: absolute;
  top: 0;
  left: 0;
  width: 80px;
  height: 24px;
  border-radius: 4px 0px;
`
export const SkeletonIconContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-left: 20px;

  ${({ theme }) => theme.mediaWidth.upToMedium`
    margin-left: 16px;
  `}
`

export const SkeletonIcon = styled(SkeletonBase)`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
`

export const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${({ width }) => width || '100px'};
  height: ${({ height }) => height || '20px'};
`

export const SkeletonButton = styled(SkeletonBase)`
  width: 100px;
  height: 40px;
  border-radius: 6px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
  `}
`

export const SkeletonIndicators = styled.div`
  position: absolute;
  bottom: 8px;
  left: 8px;
  display: flex;
  gap: 8px;
`

export const SkeletonIndicator = styled(SkeletonBase)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
`

export const MetricBadge = styled.div<{ $type: PoolType }>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 2px 8px;
  gap: 4px;
  position: absolute;
  height: 18px;
  right: 0px;
  top: 0px;
  background: ${({ $type }) => typeConfig[$type].background};
  border-radius: 3px;
  z-index: 9;
`

export const MetricText = styled(ThemedText.DeprecatedBody)<{ $type: PoolType }>`
  font-size: 10px !important;
  font-weight: 500;
  text-transform: uppercase;
  background: ${({ $type }) => typeConfig[$type].textGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-fill-color: transparent;
`
